import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-ui/components/layout';
import ProgressBar from 'tio-reimagined/components/progress-bar';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import { eq, gte, and, not } from 'tio-ui/utilities';
import {
  PhArrowLeft,
  PhArrowRight,
  PhCheck,
  PhCheckCircle,
  PhPath,
  PhTarget,
  PhLightbulb,
  PhSparkle,
  PhRocket,
  PhCalendar,
  PhClock,
  PhCurrencyDollar,
  PhGraduationCap,
  PhCertificate,
  PhBriefcase,
  PhStar,
  PhMagicWand,
  PhFlag,
  PhPencil,
  PhTrendUp,
  PhBuildings,
} from 'ember-phosphor-icons';
import PageHero from 'tio-reimagined/components/page-hero';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';

// Career goals to choose from
const HEALTHCARE_GOALS = [
  {
    id: 'nurse-manager',
    title: 'Nurse Manager',
    family: 'Nursing Leadership',
    matchScore: 92,
    timeframe: '18-24 months',
    salaryIncrease: '+45%',
    demand: 'Very High',
    openings: 8,
    recommended: true,
  },
  {
    id: 'clinical-educator',
    title: 'Clinical Nurse Educator',
    family: 'Education',
    matchScore: 88,
    timeframe: '12-18 months',
    salaryIncrease: '+30%',
    demand: 'High',
    openings: 4,
    recommended: true,
  },
  {
    id: 'charge-nurse',
    title: 'Charge Nurse',
    family: 'Nursing Leadership',
    matchScore: 95,
    timeframe: '6-12 months',
    salaryIncrease: '+20%',
    demand: 'High',
    openings: 15,
    recommended: false,
  },
  {
    id: 'nurse-practitioner',
    title: 'Nurse Practitioner',
    family: 'Advanced Practice',
    matchScore: 72,
    timeframe: '3-4 years',
    salaryIncrease: '+85%',
    demand: 'Very High',
    openings: 12,
    recommended: false,
  },
  {
    id: 'informatics-specialist',
    title: 'Clinical Informatics Specialist',
    family: 'Health IT',
    matchScore: 78,
    timeframe: '12-18 months',
    salaryIncrease: '+35%',
    demand: 'Very High',
    openings: 5,
    recommended: false,
  },
];

const UPS_GOALS = [
  {
    id: 'hub-supervisor',
    title: 'Hub Supervisor',
    family: 'Operations Leadership',
    matchScore: 78,
    timeframe: '12-18 months',
    salaryIncrease: '+20%',
    demand: 'High',
    openings: 28,
    recommended: true,
  },
  {
    id: 'operations-manager',
    title: 'Operations Manager',
    family: 'Logistics Management',
    matchScore: 66,
    timeframe: '18-24 months',
    salaryIncrease: '+30%',
    demand: 'High',
    openings: 10,
    recommended: true,
  },
  {
    id: 'feeder-driver',
    title: 'Feeder Driver (CDL-A)',
    family: 'Transportation',
    matchScore: 85,
    timeframe: '6-12 months',
    salaryIncrease: '+25%',
    demand: 'Very High',
    openings: 35,
    recommended: false,
  },
  {
    id: 'industrial-engineer',
    title: 'Industrial Engineer',
    family: 'Continuous Improvement',
    matchScore: 58,
    timeframe: '18-30 months',
    salaryIncrease: '+35%',
    demand: 'Medium',
    openings: 12,
    recommended: false,
  },
];

const STARBUCKS_GOALS = [
  {
    id: 'store-manager',
    title: 'Store Manager',
    family: 'Store Leadership',
    matchScore: 82,
    timeframe: '18-24 months',
    salaryIncrease: '+25%',
    demand: 'High',
    openings: 18,
    recommended: true,
  },
  {
    id: 'shift-supervisor',
    title: 'Shift Supervisor',
    family: 'Retail Operations',
    matchScore: 92,
    timeframe: '6-12 months',
    salaryIncrease: '+15%',
    demand: 'High',
    openings: 42,
    recommended: true,
  },
  {
    id: 'district-ops',
    title: 'District Operations Lead',
    family: 'Operations',
    matchScore: 64,
    timeframe: '24-36 months',
    salaryIncrease: '+35%',
    demand: 'Medium',
    openings: 8,
    recommended: false,
  },
  {
    id: 'supply-chain-analyst',
    title: 'Supply Chain Analyst',
    family: 'Supply Chain',
    matchScore: 58,
    timeframe: '18-30 months',
    salaryIncrease: '+30%',
    demand: 'Medium',
    openings: 10,
    recommended: false,
  },
];

// Timeline options
const TIMELINE_OPTIONS = [
  {
    id: 'aggressive',
    label: 'Fast Track',
    months: 12,
    description: 'Intensive pace, requires significant commitment',
  },
  {
    id: 'standard',
    label: 'Standard',
    months: 18,
    description: 'Balanced pace, recommended for most',
  },
  {
    id: 'flexible',
    label: 'Flexible',
    months: 24,
    description: 'Self-paced, fits around busy schedule',
  },
];

// Generated plan milestones
const HEALTHCARE_PLAN = {
  goal: 'Nurse Manager',
  timeline: '18 months',
  startDate: 'February 2025',
  targetDate: 'August 2026',
  milestones: [
    {
      id: 1,
      title: 'Complete Nurse Leadership Certificate',
      type: 'certification',
      duration: '6 months',
      startMonth: 0,
      endMonth: 6,
      status: 'current',
      details: 'ANCC-accredited program via tuition assistance',
      tuitionCost: '$2,400',
      tuitionCovered: true,
    },
    {
      id: 2,
      title: 'Epic EMR Super User Training',
      type: 'training',
      duration: '3 months',
      startMonth: 2,
      endMonth: 5,
      status: 'planned',
      details: 'Internal certification program',
      tuitionCost: 'Free',
      tuitionCovered: true,
    },
    {
      id: 3,
      title: 'Charge Nurse Rotation',
      type: 'experience',
      duration: '6 months',
      startMonth: 6,
      endMonth: 12,
      status: 'planned',
      details: 'Hands-on leadership experience',
      tuitionCost: 'N/A',
      tuitionCovered: true,
    },
    {
      id: 4,
      title: 'Healthcare Finance Basics',
      type: 'course',
      duration: '2 months',
      startMonth: 8,
      endMonth: 10,
      status: 'planned',
      details: 'Budget management essentials',
      tuitionCost: '$800',
      tuitionCovered: true,
    },
    {
      id: 5,
      title: 'Nurse Manager Shadow Program',
      type: 'experience',
      duration: '2 weeks',
      startMonth: 12,
      endMonth: 12,
      status: 'planned',
      details: 'Job shadow with current manager',
      tuitionCost: 'N/A',
      tuitionCovered: true,
    },
    {
      id: 6,
      title: 'Apply for Nurse Manager Position',
      type: 'milestone',
      duration: '1 month',
      startMonth: 16,
      endMonth: 18,
      status: 'planned',
      details: 'Target Med-Surg or ICU openings',
      tuitionCost: 'N/A',
      tuitionCovered: true,
    },
  ],
  skills: {
    willDevelop: ['Leadership', 'Budget Management', 'Staff Scheduling', 'Quality Improvement'],
    willVerify: ['Epic EMR', 'Team Management'],
  },
  investment: {
    tuitionTotal: '$3,200',
    coveredByEmployer: '$3,200',
    yourCost: '$0',
    timeCommitment: '8-10 hrs/week',
  },
};

const UPS_PLAN = {
  goal: 'Hub Supervisor',
  timeline: '16 months',
  startDate: 'March 2025',
  targetDate: 'July 2026',
  milestones: [
    {
      id: 1,
      title: 'Supervisor Leadership Program',
      type: 'training',
      duration: '10 weeks',
      startMonth: 0,
      endMonth: 3,
      status: 'current',
      details: 'Internal leadership program with coaching focus',
      tuitionCost: '$1,500',
      tuitionCovered: true,
    },
    {
      id: 2,
      title: 'Safety & Compliance Certification',
      type: 'certification',
      duration: '6 weeks',
      startMonth: 2,
      endMonth: 4,
      status: 'planned',
      details: 'OSHA and hub safety standards',
      tuitionCost: 'Free',
      tuitionCovered: true,
    },
    {
      id: 3,
      title: 'Workforce Scheduling Workshop',
      type: 'course',
      duration: '6 weeks',
      startMonth: 4,
      endMonth: 6,
      status: 'planned',
      details: 'Shift planning and staffing optimization',
      tuitionCost: '$900',
      tuitionCovered: true,
    },
    {
      id: 4,
      title: 'Lead Peak Season Shift',
      type: 'experience',
      duration: '3 months',
      startMonth: 8,
      endMonth: 11,
      status: 'planned',
      details: 'Hands-on leadership during peak season operations',
      tuitionCost: 'N/A',
      tuitionCovered: true,
    },
    {
      id: 5,
      title: 'Apply for Hub Supervisor Role',
      type: 'milestone',
      duration: '1 month',
      startMonth: 14,
      endMonth: 16,
      status: 'planned',
      details: 'Target openings in your current hub',
      tuitionCost: 'N/A',
      tuitionCovered: true,
    },
  ],
  skills: {
    willDevelop: ['Leadership', 'Scheduling', 'Operations Management'],
    willVerify: ['Safety Compliance', 'Process Improvement'],
  },
  investment: {
    tuitionTotal: '$2,400',
    coveredByEmployer: '$2,400',
    yourCost: '$0',
    timeCommitment: '6-8 hrs/week',
  },
};

const STARBUCKS_PLAN = {
  goal: 'Store Manager',
  timeline: '20 months',
  startDate: 'April 2025',
  targetDate: 'December 2026',
  milestones: [
    {
      id: 1,
      title: 'Store Manager Development',
      type: 'training',
      duration: '12 weeks',
      startMonth: 0,
      endMonth: 4,
      status: 'current',
      details: 'Leadership, operations, and financial management training',
      tuitionCost: '$1,800',
      tuitionCovered: true,
    },
    {
      id: 2,
      title: 'Retail Operations Certificate',
      type: 'course',
      duration: '8 weeks',
      startMonth: 3,
      endMonth: 5,
      status: 'planned',
      details: 'Scheduling, inventory, and service standards',
      tuitionCost: '$1,100',
      tuitionCovered: true,
    },
    {
      id: 3,
      title: 'Coffee Master Certification',
      type: 'certification',
      duration: '6 weeks',
      startMonth: 5,
      endMonth: 6,
      status: 'planned',
      details: 'Advanced coffee knowledge and quality standards',
      tuitionCost: 'Free',
      tuitionCovered: true,
    },
    {
      id: 4,
      title: 'Lead New Store Opening',
      type: 'experience',
      duration: '3 months',
      startMonth: 10,
      endMonth: 12,
      status: 'planned',
      details: 'Operational leadership for a new store launch',
      tuitionCost: 'N/A',
      tuitionCovered: true,
    },
    {
      id: 5,
      title: 'Apply for Store Manager Role',
      type: 'milestone',
      duration: '1 month',
      startMonth: 18,
      endMonth: 20,
      status: 'planned',
      details: 'Target openings in your district',
      tuitionCost: 'N/A',
      tuitionCovered: true,
    },
  ],
  skills: {
    willDevelop: ['Leadership', 'Financial Management', 'Store Operations'],
    willVerify: ['Customer Experience', 'Team Coaching'],
  },
  investment: {
    tuitionTotal: '$2,900',
    coveredByEmployer: '$2,900',
    yourCost: '$0',
    timeCommitment: '7-9 hrs/week',
  },
};

type PlanStep = 'select-goal' | 'choose-timeline' | 'review-plan' | 'confirmation';

export default class CareerPathwaysPlanNew extends Component {
  get demoClient() {
    return getCurrentDemoClientData();
  }

  get careerGoals() {
    const clientId = this.demoClient.client.id;

    if (clientId === 'ups') {
      return UPS_GOALS;
    }

    if (clientId === 'starbucks') {
      return STARBUCKS_GOALS;
    }

    return HEALTHCARE_GOALS;
  }

  get generatedPlan() {
    const clientId = this.demoClient.client.id;

    if (clientId === 'ups') {
      return UPS_PLAN;
    }

    if (clientId === 'starbucks') {
      return STARBUCKS_PLAN;
    }

    return HEALTHCARE_PLAN;
  }

  get firstMilestoneTitle() {
    return this.generatedPlan.milestones[0]?.title ?? 'your first milestone';
  }
  @tracked currentStep: PlanStep = 'select-goal';
  @tracked selectedGoal: string | null = null;
  @tracked selectedTimeline = 'standard';
  @tracked customizations: string[] = [];

  steps = [
    { id: 'select-goal', label: 'Choose Goal', number: 1 },
    { id: 'choose-timeline', label: 'Set Timeline', number: 2 },
    { id: 'review-plan', label: 'Review Plan', number: 3 },
    { id: 'confirmation', label: 'Confirm', number: 4 },
  ];

  get currentStepNumber() {
    const step = this.steps.find((s) => s.id === this.currentStep);
    return step?.number || 1;
  }

  get progressPercent() {
    return (this.currentStepNumber / this.steps.length) * 100;
  }

  get selectedGoalData() {
    return this.careerGoals.find((goal) => goal.id === this.selectedGoal);
  }

  selectGoal = (goalId: string) => {
    this.selectedGoal = goalId;
  };

  selectTimeline = (timelineId: string) => {
    this.selectedTimeline = timelineId;
  };

  nextStep = () => {
    const stepOrder: PlanStep[] = ['select-goal', 'choose-timeline', 'review-plan', 'confirmation'];
    const currentIndex = stepOrder.indexOf(this.currentStep);
    if (currentIndex < stepOrder.length - 1) {
      this.currentStep = stepOrder[currentIndex + 1] as PlanStep;
    }
  };

  previousStep = () => {
    const stepOrder: PlanStep[] = ['select-goal', 'choose-timeline', 'review-plan', 'confirmation'];
    const currentIndex = stepOrder.indexOf(this.currentStep);
    if (currentIndex > 0) {
      this.currentStep = stepOrder[currentIndex - 1] as PlanStep;
    }
  };

  getMilestoneIcon = (type: string) => {
    const icons: Record<string, unknown> = {
      certification: PhCertificate,
      training: PhGraduationCap,
      experience: PhBriefcase,
      course: PhGraduationCap,
      milestone: PhFlag,
    };
    return icons[type] || PhTarget;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0">
      {{! Header }}
      <div class="mb-6">
        <LinkTo
          @route="authenticated.reimagined.plan"
          class="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 font-medium mb-4"
        >
          <PhArrowLeft @size={{18}} />
          Back to My Plan
        </LinkTo>
        <div class="flex items-center gap-3">
          <div
            class="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center"
          >
            <PhMagicWand @size={{24}} class="text-white" />
          </div>
          <div>
            <Heading @level={{1}} class="text-2xl font-bold">Create Your Career Plan</Heading>
            <Text @variant="muted">AI-powered planning to help you reach your goals</Text>
          </div>
        </div>
      </div>

      {{! Progress Steps }}
      <div class="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div class="flex items-center justify-between mb-4">
          {{#each this.steps as |step|}}
            <div class="flex items-center">
              <div
                class="w-10 h-10 rounded-full flex items-center justify-center font-semibold
                  {{if
                    (gte this.currentStepNumber step.number)
                    'bg-violet-600 text-white'
                    'bg-gray-200 text-gray-500'
                  }}"
              >
                {{#if (gte this.currentStepNumber step.number)}}
                  {{#if (eq this.currentStepNumber step.number)}}
                    {{step.number}}
                  {{else}}
                    <PhCheck @size={{18}} />
                  {{/if}}
                {{else}}
                  {{step.number}}
                {{/if}}
              </div>
              <span
                class="ml-2 font-medium hidden sm:inline
                  {{if (eq this.currentStep step.id) 'text-violet-600' 'text-gray-500'}}"
              >
                {{step.label}}
              </span>
            </div>
            {{#unless (eq step.number 4)}}
              <div
                class="flex-1 h-1 mx-4
                  {{if (gte this.currentStepNumber step.number) 'bg-violet-400' 'bg-gray-200'}}"
              ></div>
            {{/unless}}
          {{/each}}
        </div>
        <ProgressBar @value={{this.progressPercent}} @color="blue" />
      </div>

      {{#if (eq this.currentStep "select-goal")}}
        {{! Step 1: Select Career Goal }}
        <Section class="bg-white rounded-xl shadow-sm mb-6">
          <:header>
            <div class="flex items-center gap-2">
              <PhTarget @size={{20}} class="text-violet-600" />
              <span>Choose Your Career Goal</span>
            </div>
          </:header>
          <:body>
            <Text @variant="muted" class="mb-6">
              Select the role you want to work toward. We'll create a personalized development plan
              based on your current skills and experience.
            </Text>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              {{#each this.careerGoals as |goal|}}
                <button
                  type="button"
                  {{on "click" (fn this.selectGoal goal.id)}}
                  class="text-left p-5 rounded-xl border-2 transition-all hover:shadow-md
                    {{if
                      (eq this.selectedGoal goal.id)
                      'border-violet-500 bg-violet-50'
                      'border-gray-200 hover:border-violet-300'
                    }}"
                >
                  <div class="flex items-start justify-between mb-3">
                    <div>
                      {{#if goal.recommended}}
                        <Chip @intent="success" class="mb-2">
                          <span class="inline-flex items-center gap-1">
                            <PhSparkle @size={{12}} />
                            AI Recommended
                          </span>
                        </Chip>
                      {{/if}}
                      <Heading @level={{4}}>{{goal.title}}</Heading>
                      <Text @variant="muted" @size="sm">{{goal.family}}</Text>
                    </div>
                    <div
                      class="w-14 h-14 rounded-full flex items-center justify-center font-bold text-sm
                        {{if
                          (gte goal.matchScore 90)
                          'bg-green-100 text-green-700'
                          (if
                            (gte goal.matchScore 75)
                            'bg-amber-100 text-amber-700'
                            'bg-gray-100 text-gray-600'
                          )
                        }}"
                    >
                      {{goal.matchScore}}%
                    </div>
                  </div>

                  <div class="grid grid-cols-2 gap-3 text-sm">
                    <div class="flex items-center gap-2">
                      <PhClock @size={{16}} class="text-gray-400" />
                      <span>{{goal.timeframe}}</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <PhTrendUp @size={{16}} class="text-green-500" />
                      <span class="text-green-600 font-medium">{{goal.salaryIncrease}}</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <PhBuildings @size={{16}} class="text-gray-400" />
                      <span>{{goal.openings}} openings</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <PhStar @size={{16}} class="text-amber-500" />
                      <span>{{goal.demand}} demand</span>
                    </div>
                  </div>

                  {{#if (eq this.selectedGoal goal.id)}}
                    <div
                      class="mt-3 pt-3 border-t border-violet-200 flex items-center gap-2 text-violet-600"
                    >
                      <PhCheckCircle @size={{18}} />
                      <span class="font-medium">Selected</span>
                    </div>
                  {{/if}}
                </button>
              {{/each}}
            </div>
          </:body>
        </Section>

      {{else if (eq this.currentStep "choose-timeline")}}
        {{! Step 2: Choose Timeline }}
        <Section class="bg-white rounded-xl shadow-sm mb-6">
          <:header>
            <div class="flex items-center gap-2">
              <PhCalendar @size={{20}} class="text-violet-600" />
              <span>Choose Your Timeline</span>
            </div>
          </:header>
          <:body>
            <div class="bg-violet-50 rounded-lg p-4 mb-6 flex items-center gap-4">
              <div class="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center">
                <PhTarget @size={{24}} class="text-violet-600" />
              </div>
              <div>
                <Text class="font-semibold">Goal: {{this.selectedGoalData.title}}</Text>
                <Text @variant="muted" @size="sm">
                  {{this.selectedGoalData.matchScore}}% match •
                  {{this.selectedGoalData.salaryIncrease}}
                  salary increase
                </Text>
              </div>
            </div>

            <Text @variant="muted" class="mb-6">
              How quickly do you want to reach your goal? We'll adjust your plan intensity
              accordingly.
            </Text>

            <div class="space-y-4">
              {{#each TIMELINE_OPTIONS as |option|}}
                <button
                  type="button"
                  {{on "click" (fn this.selectTimeline option.id)}}
                  class="w-full text-left p-5 rounded-xl border-2 transition-all hover:shadow-md
                    {{if
                      (eq this.selectedTimeline option.id)
                      'border-violet-500 bg-violet-50'
                      'border-gray-200 hover:border-violet-300'
                    }}"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-4">
                      <div
                        class="w-12 h-12 rounded-full flex items-center justify-center
                          {{if
                            (eq this.selectedTimeline option.id)
                            'bg-violet-500 text-white'
                            'bg-gray-100 text-gray-600'
                          }}"
                      >
                        <PhClock @size={{24}} />
                      </div>
                      <div>
                        <Heading @level={{4}}>{{option.label}}</Heading>
                        <Text @variant="muted" @size="sm">{{option.description}}</Text>
                      </div>
                    </div>
                    <div class="text-right">
                      <div class="text-2xl font-bold text-gray-900">{{option.months}}</div>
                      <Text @variant="muted" @size="sm">months</Text>
                    </div>
                  </div>
                  {{#if (eq this.selectedTimeline option.id)}}
                    <div class="mt-4 pt-4 border-t border-violet-200">
                      <div class="flex items-center justify-between text-sm">
                        <span class="text-gray-600">Estimated completion</span>
                        <span class="font-semibold">August 2026</span>
                      </div>
                      <div class="flex items-center justify-between text-sm mt-2">
                        <span class="text-gray-600">Weekly time commitment</span>
                        <span class="font-semibold">8-10 hours</span>
                      </div>
                    </div>
                  {{/if}}
                </button>
              {{/each}}
            </div>
          </:body>
        </Section>

      {{else if (eq this.currentStep "review-plan")}}
        {{! Step 3: Review AI-Generated Plan }}
        <PageHero @theme="violet">
          <:default>
            <div class="flex items-center gap-3 mb-3">
              <PhSparkle @size={{24}} />
              <Text class="font-semibold text-lg">AI-Generated Career Plan</Text>
            </div>
            <Heading @level={{2}} class="text-3xl font-bold mb-2">
              Your Path to
              {{this.generatedPlan.goal}}
            </Heading>
            <div class="flex gap-6 text-sm text-violet-200">
              <span>Start: {{this.generatedPlan.startDate}}</span>
              <span>Target: {{this.generatedPlan.targetDate}}</span>
              <span>Duration: {{this.generatedPlan.timeline}}</span>
            </div>
          </:default>
        </PageHero>

        {{! Timeline Visualization }}
        <Section class="bg-white rounded-xl shadow-sm mb-6">
          <:header>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <PhPath @size={{20}} class="text-violet-600" />
                <span>Your Development Roadmap</span>
              </div>
              <Button
                @intent="default"
                @appearance="outlined"
                @size="sm"
                @icon={{PhPencil}}
                @label="Customize"
              />
            </div>
          </:header>
          <:body>
            <div class="space-y-4">
              {{#each this.generatedPlan.milestones as |milestone|}}
                <div
                  class="flex items-start gap-4 p-4 rounded-lg
                    {{if
                      (eq milestone.status 'current')
                      'bg-violet-50 border-2 border-violet-300'
                      'bg-gray-50 border border-gray-200'
                    }}"
                >
                  <div
                    class="w-12 h-12 rounded-full flex items-center justify-center shrink-0
                      {{if
                        (eq milestone.status 'current')
                        'bg-violet-500 text-white'
                        'bg-gray-200 text-gray-600'
                      }}"
                  >
                    {{#let (this.getMilestoneIcon milestone.type) as |Icon|}}
                      {{#if Icon}}
                        {{! @glint-ignore - dynamic icon component }}
                        <Icon @size={{24}} />
                      {{/if}}
                    {{/let}}
                  </div>
                  <div class="flex-1">
                    <div class="flex items-start justify-between mb-1">
                      <div>
                        <Heading @level={{4}}>{{milestone.title}}</Heading>
                        <Text @variant="muted" @size="sm">{{milestone.details}}</Text>
                      </div>
                      {{#if (eq milestone.status "current")}}
                        <Chip @intent="primary">
                          <span class="inline-flex items-center gap-1">
                            <PhClock @size={{12}} />
                            In Progress
                          </span>
                        </Chip>
                      {{else}}
                        <Chip @intent="default">Months
                          {{milestone.startMonth}}-{{milestone.endMonth}}</Chip>
                      {{/if}}
                    </div>
                    <div class="flex items-center gap-4 mt-2 text-sm">
                      <span class="flex items-center gap-1 text-gray-500">
                        <PhClock @size={{14}} />
                        {{milestone.duration}}
                      </span>
                      {{#if milestone.tuitionCovered}}
                        <span class="flex items-center gap-1 text-green-600">
                          <PhCheckCircle @size={{14}} />
                          {{milestone.tuitionCost}}
                          covered
                        </span>
                      {{/if}}
                    </div>
                  </div>
                </div>
              {{/each}}
            </div>
          </:body>
        </Section>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {{! Skills Development }}
          <Section class="bg-white rounded-xl shadow-sm">
            <:header>
              <div class="flex items-center gap-2">
                <PhLightbulb @size={{20}} class="text-amber-500" />
                <span>Skills You'll Develop</span>
              </div>
            </:header>
            <:body>
              <div class="space-y-2">
                {{#each this.generatedPlan.skills.willDevelop as |skill|}}
                  <div class="flex items-center gap-2 p-2 bg-amber-50 rounded-lg">
                    <PhTrendUp @size={{16}} class="text-amber-600" />
                    <span>{{skill}}</span>
                  </div>
                {{/each}}
              </div>
              <div class="mt-4 pt-4 border-t">
                <Text @variant="muted" @size="sm" class="mb-2">Skills to verify:</Text>
                <div class="flex flex-wrap gap-2">
                  {{#each this.generatedPlan.skills.willVerify as |skill|}}
                    <Chip @intent="success">
                      <span class="inline-flex items-center gap-1">
                        <PhCheckCircle @size={{12}} />
                        {{skill}}
                      </span>
                    </Chip>
                  {{/each}}
                </div>
              </div>
            </:body>
          </Section>

          {{! Investment Summary }}
          <Section class="bg-white rounded-xl shadow-sm">
            <:header>
              <div class="flex items-center gap-2">
                <PhCurrencyDollar @size={{20}} class="text-green-600" />
                <span>Investment Summary</span>
              </div>
            </:header>
            <:body>
              <div class="space-y-4">
                <div class="flex justify-between items-center">
                  <span class="text-gray-600">Total tuition cost</span>
                  <span class="font-semibold">{{this.generatedPlan.investment.tuitionTotal}}</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-gray-600">Covered by employer</span>
                  <span
                    class="font-semibold text-green-600"
                  >{{this.generatedPlan.investment.coveredByEmployer}}</span>
                </div>
                <div class="flex justify-between items-center pt-3 border-t">
                  <span class="font-semibold text-lg">Your cost</span>
                  <span
                    class="font-bold text-2xl text-green-600"
                  >{{this.generatedPlan.investment.yourCost}}</span>
                </div>
                <div class="flex justify-between items-center pt-3 border-t">
                  <span class="text-gray-600">Time commitment</span>
                  <span
                    class="font-semibold"
                  >{{this.generatedPlan.investment.timeCommitment}}</span>
                </div>
              </div>
              <div class="mt-4 p-3 bg-green-50 rounded-lg">
                <Text @size="sm" class="text-green-800">
                  <strong>Great news!</strong>
                  100% of your tuition is covered through your employer's tuition assistance
                  program.
                </Text>
              </div>
            </:body>
          </Section>
        </div>

      {{else if (eq this.currentStep "confirmation")}}
        {{! Step 4: Confirmation }}
        <div class="text-center mb-8">
          <div
            class="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center"
          >
            <PhCheckCircle @size={{48}} class="text-white" />
          </div>
          <Heading @level={{1}} class="text-3xl font-bold mb-3">Your Career Plan is Ready!</Heading>
          <Text @variant="muted" class="text-lg max-w-xl mx-auto">
            You're all set to begin your journey to
            <strong>{{this.generatedPlan.goal}}</strong>. Your first milestone starts now!
          </Text>
        </div>

        <Section
          class="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl border-2 border-violet-200 mb-6"
        >
          <:body>
            <div class="text-center">
              <Heading @level={{3}} class="mb-6">What happens next?</Heading>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div class="text-center">
                  <div
                    class="w-16 h-16 mx-auto mb-3 rounded-full bg-violet-100 flex items-center justify-center"
                  >
                    <span class="text-2xl font-bold text-violet-600">1</span>
                  </div>
                  <Heading @level={{4}} class="mb-2">Enroll in First Course</Heading>
                  <Text @variant="muted" @size="sm">
                    We'll guide you through starting
                    {{this.firstMilestoneTitle}}
                  </Text>
                </div>
                <div class="text-center">
                  <div
                    class="w-16 h-16 mx-auto mb-3 rounded-full bg-violet-100 flex items-center justify-center"
                  >
                    <span class="text-2xl font-bold text-violet-600">2</span>
                  </div>
                  <Heading @level={{4}} class="mb-2">Track Your Progress</Heading>
                  <Text @variant="muted" @size="sm">
                    Your dashboard will show real-time progress and upcoming milestones
                  </Text>
                </div>
                <div class="text-center">
                  <div
                    class="w-16 h-16 mx-auto mb-3 rounded-full bg-violet-100 flex items-center justify-center"
                  >
                    <span class="text-2xl font-bold text-violet-600">3</span>
                  </div>
                  <Heading @level={{4}} class="mb-2">Get Support</Heading>
                  <Text @variant="muted" @size="sm">
                    AI Advisor and career coaches available to help you stay on track
                  </Text>
                </div>
              </div>
            </div>
          </:body>
        </Section>

        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <LinkTo @route="authenticated.reimagined.plan">
            <Button @intent="primary" @size="lg" @icon={{PhRocket}} @label="View My Plan" />
          </LinkTo>
          <Button
            @intent="default"
            @appearance="outlined"
            @size="lg"
            @icon={{PhGraduationCap}}
            @label="Start First Course"
          />
        </div>
      {{/if}}

      {{! Navigation Buttons }}
      {{#unless (eq this.currentStep "confirmation")}}
        <div class="flex items-center justify-between mt-6">
          <Button
            @intent="default"
            @appearance="outlined"
            @icon={{PhArrowLeft}}
            @label="Back"
            disabled={{eq this.currentStep "select-goal"}}
            {{on "click" this.previousStep}}
          />
          <Button
            @intent="primary"
            @icon={{if (eq this.currentStep "review-plan") PhSparkle PhArrowRight}}
            @iconPlacement="end"
            @label={{if (eq this.currentStep "review-plan") "Create My Plan" "Continue"}}
            disabled={{and (eq this.currentStep "select-goal") (not this.selectedGoal)}}
            {{on "click" this.nextStep}}
          />
        </div>
      {{/unless}}
    </VStack>
  </template>
}
